import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { useNavigate } from "react-router-dom";
import { adminClient } from "services/apiClient";

const QUICK_ACTIONS = [
  { label: "Kullanıcıları Yönet", route: "/users", color: "primary" },
  { label: "Abonelikleri İncele", route: "/subscriptions", color: "info" },
  { label: "Site Oluştur", route: "/sites", color: "success" },
  { label: "Admin Ekle", route: "/admins", color: "warning" },
  { label: "Yetki Devral", route: "/impersonation", color: "secondary" },
];

function Dashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [users, setUsers] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);

  useEffect(() => {
    let ignore = false;

    const fetchData = async () => {
      setLoading(true);
      setError("");
      try {
        const [usersRes, subsRes] = await Promise.all([
          adminClient.get("/admin/users"),
          adminClient.get("/admin/active-subscriptions"),
        ]);

        if (!ignore) {
          setUsers(usersRes.data || []);
          setSubscriptions(subsRes.data || []);
        }
      } catch (err) {
        if (!ignore) {
          setError(
            err.response?.data?.message || "Veriler alınırken bir hata oluştu."
          );
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      ignore = true;
    };
  }, []);

  const stats = useMemo(() => {
    const totalUsers = users.length;
    const pendingUsers = users.filter((user) => user.status === "pending").length;
    const activeUsers = users.filter((user) => user.status === "active").length;

    const totalSubscriptions = subscriptions.length;
    const monthly = subscriptions.filter((sub) => sub.billingCycle === "monthly").length;
    const yearly = subscriptions.filter((sub) => sub.billingCycle === "yearly").length;

    return {
      totalUsers,
      pendingUsers,
      activeUsers,
      totalSubscriptions,
      monthly,
      yearly,
    };
  }, [users, subscriptions]);

  const recentPendingUsers = useMemo(
    () => users.filter((u) => u.status === "pending").slice(0, 6),
    [users]
  );

  return (
    <DashboardLayout>
      <MDBox mb={3}>
        <MDTypography variant="h4" fontWeight="bold">
          Yönetim Paneli
        </MDTypography>
        <MDTypography variant="body2" color="text">
          En kritik işlemler ve istatistikler tek bir ekranda.
        </MDTypography>
      </MDBox>

      {error && (
        <Box mb={3}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      {loading ? (
        <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 240 }}>
          <CircularProgress />
        </Stack>
      ) : (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Kullanıcı Durumu
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Stack spacing={0.5}>
                      <Typography variant="h3">{stats.totalUsers}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Toplam
                      </Typography>
                    </Stack>
                  </Grid>
                  <Grid item xs={4}>
                    <Stack spacing={0.5}>
                      <Typography variant="h3" color="success.main">
                        {stats.activeUsers}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Aktif
                      </Typography>
                    </Stack>
                  </Grid>
                  <Grid item xs={4}>
                    <Stack spacing={0.5}>
                      <Typography variant="h3" color="warning.main">
                        {stats.pendingUsers}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Beklemede
                      </Typography>
                    </Stack>
                  </Grid>
                </Grid>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" gutterBottom>
                  Son Oluşturulan Bekleyen Kullanıcılar
                </Typography>
                {recentPendingUsers.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Bekleyen kullanıcı bulunmuyor.
                  </Typography>
                ) : (
                  <List dense disablePadding>
                    {recentPendingUsers.map((user) => (
                      <ListItem key={user.id} disablePadding>
                        <ListItemText
                          primary={user.email}
                          secondary={`Oluşturma: ${new Date(user.createdAt).toLocaleString("tr-TR")}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Aktif Abonelikler
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Stack spacing={0.5}>
                      <Typography variant="h3">{stats.totalSubscriptions}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Toplam
                      </Typography>
                    </Stack>
                  </Grid>
                  <Grid item xs={4}>
                    <Stack spacing={0.5}>
                      <Typography variant="h3" color="info.main">
                        {stats.monthly}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Aylık
                      </Typography>
                    </Stack>
                  </Grid>
                  <Grid item xs={4}>
                    <Stack spacing={0.5}>
                      <Typography variant="h3" color="secondary.main">
                        {stats.yearly}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Yıllık
                      </Typography>
                    </Stack>
                  </Grid>
                </Grid>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" gutterBottom>
                  Son Aktif Abonelikler
                </Typography>
                {subscriptions.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Aktif abonelik bulunmuyor.
                  </Typography>
                ) : (
                  <List dense disablePadding>
                    {subscriptions.slice(0, 5).map((subscription) => (
                      <ListItem key={subscription.id} disablePadding>
                        <ListItemText
                          primary={`${subscription.clientName || "Bilinmeyen"} • ${subscription.domain || "-"}`}
                          secondary={`Başlangıç: ${new Date(subscription.purchasedDate).toLocaleDateString("tr-TR")}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Hızlı İşlemler
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={2}>
                  {QUICK_ACTIONS.map((action) => (
                    <Button
                      key={action.route}
                      variant="contained"
                      color={action.color}
                      onClick={() => navigate(action.route)}
                    >
                      {action.label}
                    </Button>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      <Footer />
    </DashboardLayout>
  );
}

export default Dashboard;
